import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageCircle, Send, ArrowLeft, User, Wifi, WifiOff } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ChatPage() {
  const { chatId } = useParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(chatId || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchChats();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const connectWebSocket = useCallback((cId) => {
    if (wsRef.current) wsRef.current.close();
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${cId}?token=${token}`);
    ws.onopen = () => { setWsConnected(true); };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        fetchChats();
      } catch {}
    };
    ws.onclose = () => {
      setWsConnected(false);
      // Fallback to polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(cId), 5000);
    };
    ws.onerror = () => { setWsConnected(false); };
    wsRef.current = ws;
  }, []);

  useEffect(() => {
    if (activeChat) {
      fetchMessages(activeChat);
      if (pollRef.current) clearInterval(pollRef.current);
      connectWebSocket(activeChat);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeChat, connectWebSocket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChats = async () => {
    try {
      const res = await api.get('/chats');
      setChats(res.data.chats || []);
      if (!activeChat && res.data.chats?.length > 0) {
        setActiveChat(res.data.chats[0].id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (cId) => {
    try {
      const res = await api.get(`/chats/${cId}/messages`);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;
    setSending(true);
    try {
      if (wsConnected && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ content: newMessage.trim() }));
        setNewMessage('');
      } else {
        await api.post(`/chats/${activeChat}/messages`, { content: newMessage.trim() });
        setNewMessage('');
        fetchMessages(activeChat);
      }
      fetchChats();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const activeChatData = chats.find(c => c.id === activeChat);

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-testid="chat-page">
      <div className="h-[calc(100vh-120px)] flex rounded-2xl border border-[#E7E5E4] bg-white overflow-hidden shadow-sm">
        {/* Chat List */}
        <div className={`w-full md:w-80 border-r border-[#E7E5E4] flex flex-col ${activeChat && 'hidden md:flex'}`}>
          <div className="p-4 border-b border-[#E7E5E4]">
            <h2 className="text-lg font-bold text-[#2D2424] flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#D9381E]" /> {t('chat.title')}
            </h2>
          </div>
          <ScrollArea className="flex-1">
            {chats.length > 0 ? chats.map(chat => (
              <button
                key={chat.id}
                onClick={() => { setActiveChat(chat.id); navigate(`/chat/${chat.id}`, { replace: true }); }}
                className={`w-full p-4 flex items-center gap-3 text-left hover:bg-[#F9F5F0] transition-colors border-b border-[#E7E5E4]/50 ${
                  activeChat === chat.id ? 'bg-[#D9381E]/5' : ''
                }`}
                data-testid={`chat-item-${chat.id}`}
              >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D9381E] to-[#F2A900] flex items-center justify-center text-white font-bold flex-shrink-0">
                  {chat.other_user?.name?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-[#2D2424] truncate">{chat.other_user?.name || 'User'}</span>
                    {chat.unread_count > 0 && (
                      <span className="w-5 h-5 bg-[#D9381E] text-white rounded-full text-xs flex items-center justify-center flex-shrink-0">
                        {chat.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#78716C] truncate mt-0.5">{chat.last_message || 'Start chatting...'}</p>
                </div>
              </button>
            )) : (
              <div className="p-8 text-center">
                <MessageCircle className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
                <p className="text-sm text-[#78716C]">{t('chat.no_chats')}</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Message Area */}
        <div className={`flex-1 flex flex-col ${!activeChat && 'hidden md:flex'}`}>
          {activeChat && activeChatData ? (
            <>
              <div className="p-4 border-b border-[#E7E5E4] flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setActiveChat(null)} className="md:hidden">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D9381E] to-[#F2A900] flex items-center justify-center text-white font-bold">
                  {activeChatData.other_user?.name?.[0] || '?'}
                </div>
                <div>
                  <div className="font-semibold text-sm text-[#2D2424]">{activeChatData.other_user?.name}</div>
                  <div className="text-xs text-[#78716C] flex items-center gap-1">
                    {wsConnected ? <><Wifi className="w-3 h-3 text-green-500" /> Live</> : <><WifiOff className="w-3 h-3 text-[#78716C]" /> {activeChatData.other_user?.city}</>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="ml-auto text-[#D9381E]"
                  onClick={() => navigate(`/profile/${activeChatData.other_user?.id}`)} data-testid="view-chat-profile">
                  View Profile
                </Button>
              </div>

              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 text-sm ${
                        msg.sender_id === user?.id ? 'chat-bubble-sent' : 'chat-bubble-received'
                      }`}>
                        {msg.content}
                        <div className={`text-[10px] mt-1 ${msg.sender_id === user?.id ? 'text-white/60' : 'text-[#78716C]'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <form onSubmit={handleSend} className="p-4 border-t border-[#E7E5E4] flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('chat.placeholder')}
                  className="flex-1 h-11 rounded-full border-[#E7E5E4]"
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !newMessage.trim()}
                  className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full w-11 h-11 p-0"
                  data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-[#E7E5E4] mx-auto mb-4" />
                <p className="text-[#78716C]">Select a conversation to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
